import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { SteamSystem } from './steam-system.js';

export class EmberBackground extends Component {
  /** @type {SteamSystem|null} */
  #steamSystem = null;
  #width = 0;
  #height = 0;
  #dpr = 1;
  /** @type {number|null} */
  #frameId = null;
  /** @type {ResizeObserver|null} */
  #resizeObserver = null;
  #isMobile = false;

  connectedCallback() {
    super.connectedCallback();

    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#isMobile = isMobileBreakpoint();

    this.#steamSystem = new SteamSystem(
      /** @type {HTMLCanvasElement} */ (this.refs.steamCanvas),
      this.#isMobile,
    );
    this.#steamSystem.observe(this);

    this.#resize();
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    this.#animate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#frameId !== null) {
      cancelAnimationFrame(this.#frameId);
      this.#frameId = null;
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    if (this.#steamSystem) {
      this.#steamSystem.dispose();
      this.#steamSystem = null;
    }
  }

  #resize() {
    const rect = this.getBoundingClientRect();
    this.#width = rect.width;
    this.#height = rect.height;
    if (this.#steamSystem) {
      this.#steamSystem.resize(this.#width, this.#height, this.#dpr);
    }
  }

  #handleBreakpointChange = () => {
    const wasMobile = this.#isMobile;
    this.#isMobile = isMobileBreakpoint();

    if (this.#steamSystem && this.#isMobile !== wasMobile) {
      this.#steamSystem.dispose();
      this.#steamSystem = new SteamSystem(
        /** @type {HTMLCanvasElement} */ (this.refs.steamCanvas),
        this.#isMobile,
      );
      this.#steamSystem.resize(this.#width, this.#height, this.#dpr);
      this.#steamSystem.observe(this);
    }
  };

  /** Called by sauna-splash to trigger a steam surge */
  triggerSurge(normalizedX = 0.5) {
    if (this.#steamSystem) {
      this.#steamSystem.triggerSurge(normalizedX);
    }
  }

  #animate = () => {
    this.#frameId = requestAnimationFrame(this.#animate);
    if (this.#steamSystem) {
      this.#steamSystem.update(1 / 60);
      this.#steamSystem.draw();
    }
  };
}

if (!customElements.get('ember-background')) {
  customElements.define('ember-background', EmberBackground);
}
